import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Info, MessageCircle } from "lucide-react";
import { cachedGet } from "@/lib/api";
import { waLink } from "@/App";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";

export const PACKAGES = [
  { id: "hyd-35", group: "Within Hyderabad", name: "The Intimate Satsang", price: 35000,
    lineup: ["1 Male Singer", "Male Singer on Keyboard", "Dholak + Darbuka", "Octopad"] },
  { id: "hyd-45", group: "Within Hyderabad", name: "The Full Sandhya", price: 45000,
    lineup: ["2 Male Singers", "Keyboard", "Dholak + Darbuka", "Flute + Guitar", "Octopad"] },
  { id: "hyd-75", group: "Within Hyderabad", name: "The Grand Utsav", price: 75000, featured: true,
    lineup: ["2 Male Singers", "1 Female Singer", "Keyboard", "Dholak + Darbuka", "Flute + Guitar", "Octopad", "Drums"] },
  { id: "out-60", group: "Outside Hyderabad", name: "5-Member Team", price: 60000,
    lineup: ["2 Lead Singers (Guitar + Piano)", "Drums with Pad", "Dholak with Darbuka", "Band Manager & Anchor"] },
  { id: "out-80", group: "Outside Hyderabad", name: "8-Member Team", price: 80000,
    lineup: ["Everything in the 5-Member Team", "1 Female Singer", "Chorus"] },
  { id: "out-100", group: "Outside Hyderabad", name: "9-Member Team", price: 100000, featured: true,
    lineup: ["Everything in the 8-Member Team", "Flute", "Electric Guitar"] },
];

export const packageLabel = (p) => p.name;

const TERMS = [
  "50% advance confirms your booking; balance on the day of the event",
  "For events outside Hyderabad, travel & hospitality are the client's responsibility",
  "Client arranges the sound system as per our technical rider and provides a drum kit",
];

function PackageCard({ pkg, index, onReserve }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`h-full flex flex-col border p-8 md:p-10 transition-colors ${pkg.featured ? "border-saffron/60 bg-surface" : "border-bone/10 bg-ink hover:border-bone/25"}`}
      data-testid={`package-card-${pkg.id}`}
    >
      {pkg.featured && (
        <div className="flex justify-end">
          <span className="text-[10px] font-display tracking-[0.2em] text-ink bg-gold px-2 py-1">POPULAR</span>
        </div>
      )}
      <h3 className="mt-4 font-serif text-3xl text-bone">{pkg.name}</h3>
      <p className="mt-2 font-serif text-4xl text-gold" data-testid={`package-price-${pkg.id}`}>₹{pkg.price.toLocaleString("en-IN")}</p>
      <a href={waLink(`Hi The Band Sumiran! I'd like a quote for the ${pkg.name} package.`)}
        target="_blank" rel="noreferrer"
        className="mt-2 inline-flex items-center gap-2 w-fit text-xs text-[#25D366] border border-[#25D366]/50 px-3 py-1.5 hover:bg-[#25D366] hover:text-ink transition-colors"
        data-testid={`package-quote-${pkg.id}`}>
        <MessageCircle size={12} /> Get Quote on WhatsApp
      </a>
      <p className="mt-6 font-display text-[11px] tracking-[0.25em] text-saffron uppercase">What's included</p>
      <ul className="mt-3 space-y-2.5 flex-1">
        <li className="flex items-start gap-2.5 text-sm text-bone/70">
          <Check size={14} className="text-saffron mt-0.5 shrink-0" /> 2.5 to 3 hours live performance
        </li>
        <li className="flex items-start gap-2.5 text-sm text-saffron/90">
          <Info size={14} className="text-saffron mt-0.5 shrink-0" /> Sound setup must be provided by the client
        </li>
        {pkg.lineup.map((l) => (
          <li key={l} className="flex items-start gap-2.5 text-sm text-bone/70">
            <Check size={14} className="text-saffron mt-0.5 shrink-0" /> {l}
          </li>
        ))}
      </ul>
      <button onClick={() => onReserve(pkg)}
        className="group mt-8 flex items-center gap-2 text-sm text-saffron hover:text-gold transition-colors w-fit"
        data-testid={`package-reserve-${pkg.id}`}>
        Reserve this package <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
      </button>
    </motion.div>
  );
}

export default function Packages({ onReserve }) {
  const [live, setLive] = useState(null);

  useEffect(() => {
    cachedGet("/packages").then(({ data }) => setLive(data.length ? data : null)).catch(() => setLive(null));
  }, []);

  // Uniform pricing — every visitor sees the same packages at the same price,
  // regardless of where the event is. Sorted by price for easy comparison.
  const shown = [...(live || PACKAGES)].sort((a, b) => a.price - b.price);

  return (
    <section id="packages" className="px-6 md:px-12 py-24 md:py-32 bg-surface/50" data-testid="packages-section">
      <SectionHeading overline="Private Show Packages" title="Choose your" italic="ensemble" testid="packages-heading" />
      <Reveal delay={0.1}>
        <p className="mt-6 text-ash text-sm max-w-xl">
          Every gathering is different — pick a lineup and we'll quote you on WhatsApp in minutes.
          Same packages and pricing across India.
        </p>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="packages-grid">
        {shown.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} onReserve={onReserve} />)}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-14 border border-bone/10 bg-ink p-8 md:p-10" data-testid="booking-terms">
          <p className="flex items-center gap-2 font-display text-xs tracking-[0.3em] text-ash uppercase"><Info size={14} className="text-saffron" /> Booking Terms & Logistics</p>
          <ul className="mt-5 space-y-3">
            {TERMS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-bone/70">
                <span className="w-1.5 h-1.5 bg-saffron mt-1.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </section>
  );
}
